import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
  PrimaryButtonMedium as PrimaryButton,
  TextButtonSmall as TextButton,
} from '@xingternal/malt-button';
import { H } from '@xingternal/malt-typography';
import UserImage from '@xingternal/malt-user-image';
import { testable } from '@xingternal/malt-util';
import { QA_MALT } from '@xingternal/malt-constants';

import StretchButtonGroup from './../../Components/StretchButtonGroup/StretchButtonGroup';
import PasswordField from './../../Components/PasswordField/PasswordField';
import FormMessage from './../../Components/FormMessage/FormMessage';
import CenterImage from './../../Components/CenterImage/CenterImage';

import styles from './LoginKnownUser.css';
import messages from './messages';

const getSalutationKey = gender =>
  gender === 'm' ? messages.salutationMale : messages.salutationFemale;

const TestableForm = testable(QA_MALT, 'form');

const LoginKnownUser = ({
  handleChange,
  handleSubmit,
  oldLoginAction,
  forgotPasswordAction,
  isSubmitting,
  errors,
  passwordRef,
  user: { email, lastName, firstName, gender, imageUrl },
  formMessage: { notice, error },
  didResetPassword,
  setErrors,
}) => {
  useEffect(() => {
    if (error) {
      setErrors({ credentials: error });
    }
  }, []);

  return (
    <>
      <CenterImage
        image={
          <UserImage
            userName={'current-user'}
            src={imageUrl + '.96x96.jpg'}
            size={'400'}
            gender={gender}
            className={styles.userImage}
          />
        }
      />
      <H
        testId={'login-known-user-headline'}
        level={1}
        className={styles.userSalutation}
      >
        {didResetPassword ? (
          <FormattedMessage {...messages.postRecoveryTitle} />
        ) : (
          <>
            <FormattedMessage {...messages.salutationText} />
            <br />
            <FormattedMessage
              {...getSalutationKey(gender)}
              values={{
                lastName,
                firstName,
              }}
            />
          </>
        )}
      </H>
      <TestableForm
        onSubmit={handleSubmit}
        method={'POST'}
        testId={'login-known-user-form'}
      >
        <PasswordField
          autoFocus
          label={<FormattedMessage {...messages.passwordLabel} />}
          inputRef={passwordRef}
          handleChange={handleChange}
        />

        <div className={styles.rightAlignContainer}>
          <TextButton
            onClick={() => forgotPasswordAction(email)}
            noPadding
            testId={'login-forgot-password-link'}
          >
            <FormattedMessage {...messages.forgotPasswordLabel} />
          </TextButton>
        </div>

        <FormMessage notice={notice} error={errors.credentials} />

        <StretchButtonGroup>
          <PrimaryButton
            testId={'login-known-user-submit'}
            type={'submit'}
            loading={isSubmitting}
          >
            <FormattedMessage {...messages.buttonLabel} />
          </PrimaryButton>
          <TextButton
            onClick={oldLoginAction}
            noPadding
            testId={'login-button-not-me'}
          >
            <FormattedMessage {...messages.notYouLink} />
          </TextButton>
        </StretchButtonGroup>
      </TestableForm>
    </>
  );
};

LoginKnownUser.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  oldLoginAction: PropTypes.func.isRequired,
  forgotPasswordAction: PropTypes.func.isRequired,
  formMessage: PropTypes.object.isRequired,
  passwordRef: PropTypes.object,
  isSubmitting: PropTypes.bool,
  user: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    gender: PropTypes.string,
    imageUrl: PropTypes.string,
  }),
  errors: PropTypes.object,
  values: PropTypes.object.isRequired,
  didResetPassword: PropTypes.bool,
  setErrors: PropTypes.func.isRequired,
};

export default injectIntl(LoginKnownUser);
